import React, { useState, forwardRef, useRef } from 'react';
import TextField, { BaseTextFieldProps } from '@mui/material/TextField';
import styled from '@emotion/styled';
import { borderRadius, color, fontSize, fontWeight, lineHeight, opacity, spacing } from '@/theme/tokens';
import { InputAdornment, Typography } from '@mui/material';
import IconButtonGray from '@/components/formElements/iconButtons/IconButtonGray';
import IconCancelFilled from '@/assets/icons/IconCancelFilled';
import IconSearch from '@/assets/icons/IconSearch';
import { Asterisk } from '../Asterisk';

interface TextFieldFilledProps extends BaseTextFieldProps {
    label?: string;
    value?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    readOnly?: boolean;
    error?: boolean;
    helperText?: string;
    type?: string;
    InputProps?: any;
    maxLength?: number;
    onSearchClick?: () => void;
    onClearClick?: () => void;
    isRequired?: boolean;
    alwaysShowStartAdornment?: boolean;
}

const StyledTextField = styled(TextField)<{ readOnly?: boolean }>`
    & .MuiInputBase-root {
        height: 46px;
        background-color: ${color.gray[10]} !important;
        color: ${color.gray[80]};
        font-size: ${fontSize[15]};
        font-weight: ${fontWeight.regular};
        line-height: ${lineHeight[130]};
        border-radius: ${borderRadius[8]};
        border: 1px solid transparent;
        transition: border-color 120ms ease, box-shadow 120ms ease;
        padding: 6px ${spacing[12]};

        &.MuiInputBase-adornedEnd {
            padding: ${spacing[6]} ${spacing[2]} ${spacing[6]} ${spacing[12]};
        }

        & input[type='search'] {
            &::-webkit-search-cancel-button,
            &::-webkit-search-decoration {
                -webkit-appearance: none;
                appearance: none;
            }
        }
        &:before {
            border-bottom: none;
        }
        &:after {
            border-bottom: none;
        }
        &:hover:before {
            border-bottom: none !important;
        }
        &:hover {
            background-color: ${color.gray[10]};
        }

        &.Mui-focused {
            border-color: ${color.gray[80]};
            background-color: ${color.gray[10]};
            box-shadow: 0 0 0 2px ${color.black[5]};
        }
        &.Mui-error {
            border-color: ${color.brand[60]};
        }
        &.Mui-error.Mui-focused {
            border-color: ${color.brand[60]} !important;
            box-shadow: 0 0 0 2px ${color.brand[10]} !important;
        }
        & .Mui-disabled {
            background-color: ${color.gray[10]} !important;
        }
    }
    & .MuiFormLabel-root {
        font-size: ${fontSize[15]};
        font-weight: ${fontWeight.regular};
        line-height: ${lineHeight[130]};
    }
    & .MuiInputLabel-root {
        &.Mui-error {
            color: ${color.brand[60]} !important;
        }
    }
    & .MuiInputLabel-root:not(.MuiInputLabel-shrink) {
        transform: translate(12px, 14px) scale(1);
        color: ${color.gray[50]};
    }
    & .MuiInputLabel-root.MuiInputLabel-shrink {
        color: ${color.gray[80]};
        transform: translate(12px, 6px) scale(0.8);
    }
    & .MuiOutlinedInput-root {
        & fieldset {
            border-color: ${color.black[0]};
        }
        &.Mui-focused fieldset {
            border-width: 2px;
        }
    }
    & .MuiInputBase-input {
        padding: 0;
        height: 100%;
        display: flex;
        align-items: center;
        &::placeholder {
            color: ${color.gray[50]};
            opacity: 1;
        }
    }
    & .MuiFormHelperText-root {
        font-size: ${fontSize[12]};
        line-height: ${lineHeight[130]};
        font-weight: ${fontWeight.regular};
        color: ${color.gray[80]};
        margin: ${spacing[4]} ${spacing[12]} 0 ${spacing[12]};
    }
    & .MuiInputBase-adornedStart {
        padding-left: 0;

        & .MuiInputBase-input {
            padding-left: ${spacing[6]};
        }
    }
    & .MuiInputAdornment-root {
        margin: 0;
        height: 100%;
        display: flex;
        align-items: center;
    }
    & .MuiInputLabel-shrink + .MuiInputBase-root .MuiInputBase-input {
        padding-top: 14px;
    }
    & .MuiFormLabel-asterisk {
        color: ${color.brand[50]};
    }
    & .Mui-disabled {
        opacity: ${props => (props.readOnly ? opacity[100] : opacity[70])} !important;
        &:before {
            border-bottom: none !important;
        }
        &:after {
            border-bottom: none !important;
        }
    }
    & .Mui-readOnly {
        -webkit-text-fill-color: ${color.gray[80]};
    }
`;

const TextFieldFilled = forwardRef<HTMLInputElement, TextFieldFilledProps>(
    (
        {
            label,
            value,
            onChange,
            disabled = false,
            readOnly = false,
            error = false,
            helperText,
            type,
            InputProps,
            maxLength,
            onSearchClick,
            onClearClick,
            isRequired,
            alwaysShowStartAdornment = false,
            ...rest
        },
        ref
    ) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const [isFocused, setIsFocused] = useState(false);
        const handleClear = () => {
            onClearClick?.();
            onChange?.({
                target: { value: '' },
            } as React.ChangeEvent<HTMLInputElement>);
        };

        const customInputProps = {
            ...InputProps,
            startAdornment:
                InputProps?.startAdornment && (alwaysShowStartAdornment || isFocused || value) ? (
                    <InputAdornment
                        position="start"
                        sx={{
                            margin: 0,
                            paddingLeft: spacing[12],
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: spacing[1],
                        }}
                    >
                        <Typography variant="label5regular" color={color.gray[80]}>
                            {InputProps?.startAdornment}
                        </Typography>
                    </InputAdornment>
                ) : null,
            onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                setIsFocused(true);
                InputProps?.onFocus?.(e);
            },
            onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                setIsFocused(false);
                InputProps?.onBlur?.(e);
            },
        };

        return (
            <StyledTextField
                readOnly={readOnly}
                label={
                    isRequired ? (
                        <>
                            {label} <Asterisk variant="label5medium">*</Asterisk>
                        </>
                    ) : (
                        label
                    )
                }
                value={value}
                onChange={onChange}
                variant="filled"
                fullWidth
                disabled={disabled}
                InputProps={{
                    readOnly,
                    autoComplete: 'off',
                    endAdornment:
                        type === 'search' ? (
                            <InputAdornment
                                position="end"
                                sx={{
                                    margin: 0,
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <>
                                    {value && (
                                        <IconButtonGray
                                            onClick={handleClear}
                                            disabled={disabled}
                                            sx={{
                                                height: '34px',
                                                width: '34px',
                                                padding: '6px',
                                            }}
                                        >
                                            <IconCancelFilled color={color.gray[70]} width={22} height={22} />
                                        </IconButtonGray>
                                    )}
                                    {onSearchClick && (
                                        <IconButtonGray sx={{ padding: '6px' }} onClick={onSearchClick} disabled={disabled}>
                                            <IconSearch width={22} height={22} color={color.gray[70]} />
                                        </IconButtonGray>
                                    )}
                                </>
                            </InputAdornment>
                        ) : (
                            InputProps?.endAdornment
                        ),
                    ...customInputProps,
                }}
                error={error}
                helperText={helperText}
                ref={inputRef}
                inputProps={{ maxLength }}
                {...rest}
            />
        );
    }
);

TextFieldFilled.displayName = 'TextFieldFilled';

export default TextFieldFilled;
